import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { SOLUTION_CARDS } from "@/components/home/Solutions";

const PAGES = {
  "golf-course-lighting": {
    eyebrow: "Golf Course Lighting — Arizona & Nationwide",
    title: "Night Golf, Engineered Properly",
    img: "/assets/hero/frame-5.jpg",
    lede: "Professional LED systems that turn fairways, greens, and practice areas into genuine nighttime playing surfaces — designed around playability, turf health, and your neighbors.",
    body: [
      "Night golf is no longer a novelty — it is an operational strategy. Properly engineered lighting lets a course extend tee sheets into the evening, host twilight leagues and events, and offer members something genuinely new under the lights.",
      "Fairlume designs each project around the course itself: pole placement that respects sightlines and play, optics that put light on the playing surface rather than the sky, and controls that let operators run only the holes they need.",
      "Every project begins with a site assessment covering coverage goals, power availability, and local considerations — then a custom proposal with transparent commercial terms.",
    ],
    points: [
      "Fairway, green, and practice-area illumination",
      "Optics engineered for ball-flight visibility",
      "Consideration for spill light and neighboring properties",
      "Phased installations — start with signature holes",
      "Recommendations for trusted installation contractors",
    ],
  },
  "driving-range-lighting": {
    eyebrow: "Driving Range Lighting",
    title: "Keep the Bays Full After Dark",
    img: "/assets/solutions/driving-range-night.jpg",
    lede: "Evening practice is prime time. Commercial LED range lighting keeps ball flight visible from the mat to the back fence — and keeps your facility earning after sunset.",
    body: [
      "For many golfers, after work is the only time to practice. A well-lit range captures that demand: clear ball tracking, evenly lit target greens, and comfortable, glare-controlled bays.",
      "Modern LED systems deliver instant-on performance, dramatically lower energy draw than legacy metal halide, and precise beam control that keeps light on the range and out of the neighborhood.",
      "Fairlume guides everything from coverage planning through contractor recommendations, whether you're lighting a small practice tee or a multi-tier entertainment range.",
    ],
    points: [
      "Full ball-flight visibility to the boundary",
      "Even, glare-controlled illumination at the bays",
      "Instant-on LED — no warm-up delays",
      "Target green and landing-zone accent lighting",
      "Energy-efficient operation for nightly use",
    ],
  },
  "sports-lighting": {
    eyebrow: "Sports Facility Lighting",
    title: "Fields Ready for Evening Play",
    img: "/assets/solutions/sports-field-night.jpg",
    lede: "Athletic fields, practice areas, and outdoor venues lit for training, league play, and community events — safely and uniformly.",
    body: [
      "Participation doesn't stop at sunset, and neither should your facility. Professionally planned sports lighting extends scheduling capacity for schools, clubs, municipalities, and private complexes.",
      "Fairlume approaches every venue with uniformity, glare control, and player safety as the design baseline — then layers in the practical realities of budget, power, and phasing.",
      "From single practice fields to multi-field complexes, we source the right systems and recommend qualified independent contractors to install them.",
    ],
    points: [
      "Uniform illumination for safe, quality play",
      "Glare-controlled optics for players and neighbors",
      "Solutions for fields, courts, and practice areas",
      "Phased upgrade paths from legacy systems",
      "Scheduling flexibility for leagues and events",
    ],
  },
  "commercial-lighting": {
    eyebrow: "Commercial Outdoor Lighting",
    title: "Properties That Perform After Dark",
    img: "/assets/solutions/commercial-outdoor-night.jpg",
    lede: "Large-scale outdoor illumination for commercial properties, resorts, and grounds — where safety, aesthetics, and efficiency meet.",
    body: [
      "Outdoor lighting shapes how a property feels and functions after dark: welcoming approaches, safe circulation, and architecture that holds its presence at night.",
      "Fairlume plans commercial outdoor lighting at the property scale — from resorts and hospitality grounds to large commercial facilities — with efficient LED systems and thoughtful controls.",
      "Every engagement starts with your objectives: security, guest experience, brand presence, or all three. The plan follows.",
    ],
    points: [
      "Property-wide illumination planning",
      "Architectural and landscape accent lighting",
      "Safety and circulation lighting for guests and staff",
      "High-efficiency LED systems with smart controls",
      "Recommendations for trusted installation contractors",
    ],
  },
};

export default function SolutionPage() {
  const { slug } = useParams();
  const page = PAGES[slug];

  if (!page) {
    return (
      <main className="pt-40 pb-32 px-6 text-center">
        <h1 className="font-display text-3xl">Solution not found</h1>
        <Link to="/" className="text-soft text-sm mt-4 inline-block">Return home</Link>
      </main>
    );
  }

  return (
    <main data-testid={`solution-page-${slug}`}>
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden grain">
        <img src={page.img} alt={page.title} className="absolute inset-0 w-full h-full object-cover opacity-75" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#101214] via-[#101214]/40 to-[#101214]/60" />
        <div className="relative z-10 h-full flex flex-col justify-end px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto pb-16">
          <Reveal>
            <Eyebrow>{page.eyebrow}</Eyebrow>
            <h1 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
              {page.title}
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 sm:py-28 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-lg sm:text-xl text-white/85 leading-relaxed">{page.lede}</p>
            </Reveal>
            {page.body.map((p, i) => (
              <Reveal key={i} delay={0.08 * (i + 1)}>
                <p className="mt-8 text-muted-green text-base leading-relaxed">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.35}>
              <Link
                to="/quote"
                data-testid={`solution-quote-cta-${slug}`}
                className="mt-12 inline-flex items-center gap-3 bg-[#A7D7A0] text-[#101214] font-semibold px-8 py-4 rounded-sm hover:bg-white transition-colors duration-300 group"
              >
                Request a Quote
                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="bg-card-dark border hairline rounded-md p-8 sm:p-10 sticky top-28">
                <h2 className="font-mono-tech text-xs uppercase tracking-[0.25em] text-soft mb-8">What We Deliver</h2>
                <ul className="space-y-5">
                  {page.points.map((pt) => (
                    <li key={pt} className="flex gap-4 items-start">
                      <Check size={16} className="text-[#A7D7A0] mt-1 shrink-0" strokeWidth={2.5} />
                      <span className="text-sm text-white/75 leading-relaxed">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Other solutions */}
      <section className="py-20 border-t hairline px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <Eyebrow>Explore More</Eyebrow>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8">
          {SOLUTION_CARDS.filter((s) => !s.to.endsWith(slug)).slice(0, 3).map((s) => (
            <Link key={s.to} to={s.to} data-testid={`related-${s.to.split("/").pop()}`} className="group border hairline rounded-md p-7 hover:bg-white/[0.03] transition-colors duration-500">
              <h3 className="font-display font-semibold text-lg tracking-tight group-hover:text-[#A7D7A0] transition-colors">{s.title}</h3>
              <span className="mt-4 inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-white/40 group-hover:text-[#A7D7A0] transition-colors">
                Explore <ArrowUpRight size={13} />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}
